<template>
  <v-dialog v-model="dialog" persistent max-width="760" :fullscreen="$vuetify.breakpoint.xsOnly"
    content-class="create-pay-dialog">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-bind="attrs" v-on="on" class="create-pay-trigger" color="success" rounded>
        <v-icon left small>mdi-cash-check</v-icon>Create pay
      </v-btn>
    </template>

    <v-card class="create-pay-card">
      <header class="create-pay-header">
        <div class="create-pay-header__icon"><v-icon>mdi-cash-check</v-icon></div>
        <div>
          <small>Payroll confirmation</small>
          <h2>Create employee pay</h2>
          <p>Review the hours and amount carefully before recording payment.</p>
        </div>
        <v-btn class="create-pay-close" icon @click="close" aria-label="Close create pay">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="create-pay-scroll">
        <section class="pay-employee-card">
          <div class="pay-employee-avatar">
            {{ employee.name ? employee.name.charAt(0).toUpperCase() : "E" }}
          </div>
          <div class="pay-employee-copy">
            <small>Paying employee</small>
            <strong>{{ employee.name }}</strong>
            <span v-if="period">
              <v-icon small>mdi-calendar-range</v-icon>
              {{ period.start | moment("MMM D, YYYY") }} – {{ period.end | moment("MMM D, YYYY") }}
            </span>
          </div>
          <div class="pay-employee-total">
            <small>Calculated pay</small>
            <strong>{{ grandTotal | currency }}</strong>
          </div>
        </section>

        <section class="pay-review-section">
          <div class="pay-section-heading">
            <div><small>Step 1</small><h3>Review hours and earnings</h3></div>
            <v-chip v-if="employee.payOverTime" class="pay-overtime-chip" small>
              <v-icon left small>mdi-timer-outline</v-icon>Overtime enabled
            </v-chip>
          </div>

          <div class="pay-metrics">
            <div>
              <span>Worked hours</span>
              <strong>{{ secondsToHms(totalSeconds) || "0 h" }}</strong>
            </div>
            <div>
              <span>Expected hours</span>
              <strong>{{ secondsToHms(workingSeconds) || "0 h" }}</strong>
            </div>
            <div>
              <span>Hourly rate</span>
              <strong>{{ employee.hourlyRate | currency }}</strong>
            </div>
            <div>
              <span>Daily target</span>
              <strong v-if="employee.metadata && employee.metadata.timeClockLogin &&
                employee.metadata.timeClockLogin.workingHours">
                {{ employee.metadata.timeClockLogin.workingHours }} hours
              </strong>
              <strong v-else>12 hours <small>default</small></strong>
            </div>
          </div>

          <div :class="['pay-variance', diffTime() > 0 ? 'is-short' : 'is-over']">
            <v-icon>{{ diffTime() > 0 ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}</v-icon>
            <span>{{ diffTime() > 0 ? "Below expected hours" : "Above expected hours" }}</span>
            <strong>{{ secondsToHms(Math.abs(diffTime())) || "0 h" }}</strong>
          </div>

          <div v-if="employee.payOverTime" class="overtime-summary">
            <span>Overtime begins after <strong>{{ employee.startOverTime }} hours</strong></span>
            <span v-if="overtimeHours > 0"><strong>{{ secondsToHms(overtimeHours) }}</strong> overtime worked</span>
            <span>Overtime rate <strong>{{ employee.overTimeRate | currency }}</strong></span>
          </div>

          <div class="pay-breakdown">
            <div><span>Regular pay</span><strong>{{ regularPay | currency }}</strong></div>
            <div v-if="employee.payOverTime && overtimeHours > 0">
              <span>Overtime pay</span><strong>{{ overtimePay | currency }}</strong>
            </div>
            <div class="pay-breakdown__total">
              <span>Total calculated pay</span><strong>{{ totalPayWithOvertime | currency }}</strong>
            </div>
          </div>
        </section>

        <section class="pay-entry-section">
          <div class="pay-section-heading">
            <div><small>Step 2</small><h3>Payment details</h3></div>
          </div>
          <p class="pay-section-help">Confirm the date and final amount that should be recorded.</p>
          <div class="pay-entry-grid">
            <div class="pay-date-field">
              <label>Payment date</label>
              <DateTimeTextField hide-details v-model="pay.entryAt" :initDate="pay.entryAt" />
            </div>
            <v-text-field prefix="$" outlined type="number" v-model="pay.amount"
              :error-messages="amountError" label="Payment amount" @focus="$event.target.select()"
              hint="You may adjust the final recorded amount" persistent-hint></v-text-field>
          </div>
          <v-textarea class="pay-note-field" label="Payment note (optional)" outlined rows="3"
            auto-grow clearable v-model="pay.note"
            hint="Add a check number, adjustment explanation, or internal note" persistent-hint></v-textarea>
        </section>

        <div class="pay-confirmation-note">
          <v-icon>mdi-shield-check-outline</v-icon>
          <div>
            <strong>This action records payment for the included shifts</strong>
            <span>{{ payableShiftCount }} completed {{ payableShiftCount === 1 ? "shift" : "shifts" }} will be linked to this pay record.</span>
          </div>
        </div>
      </div>

      <footer class="create-pay-actions">
        <v-btn text large :disabled="loading" @click="close">Cancel</v-btn>
        <v-btn color="success" rounded large :loading="loading" :disabled="!isAmountValid" @click="submit">
          <v-icon left>mdi-check-circle-outline</v-icon>Record payment
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
// import timeClockService from "@/modules/TimeClock/service";
import DateTimeTextField from "@/components/DateTimeTextField.vue";
import payService from "@/modules/Pay/service";

export default {
  props: [
    "employee",
    "grandTotal",
    "period",
    "totalSeconds",
    "workingSeconds",
    "overtimeHours",
    "regularPay",
    "overtimePay",
    "totalPayWithOvertime",
  ],
  data: (vm) => ({
    dialog: false,
    loading: false,
    pay: {
      entryAt: vm.$moment(),
      amount: 0,
      note: null,
      PaidToEmployeeId: vm.employee.id,
    },
  }),
  components: {
    DateTimeTextField,
  },
  computed: {
    isAmountValid() {
      return Number(this.pay.amount) > 0;
    },
    amountError() {
      if (this.pay.amount === null || this.pay.amount === "") {
        return ["Payment amount is required"];
      }
      if (Number(this.pay.amount) <= 0) {
        return ["Payment amount must be greater than zero"];
      }
      return [];
    },
    payableShiftCount() {
      if (!this.employee || !Array.isArray(this.employee.TimeClocks)) return 0;
      return this.employee.TimeClocks.filter(
        (row) => row.status === "clockedOut"
      ).length;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.pay.amount = Number(this.grandTotal || 0).toFixed(2);
        this.pay.PaidToEmployeeId = this.employee.id;
      }
    },
  },
  methods: {
    async submit() {
      if (!this.isAmountValid || this.loading) return false;
      this.loading = true;
      console.log("pay", this.pay);
      // console.log("employee", this.employee);
      // console.log("employee", this.employee.TimeClocks.length);
      // return false
      let timeClock = [];
      await Promise.all(
        (this.employee.TimeClocks || []).map((row) => {
          if (row.status === "clockedOut") timeClock.push(row.id);
        })
      );
      this.pay.metadata = {
        totalSeconds: this.totalSeconds,
        hourlyRate: this.employee.hourlyRate,
        payOverTime: this.employee.payOverTime,
        startOverTime: this.employee.startOverTime,
        overTimeRate: this.employee.overTimeRate,
        workingSeconds: this.workingSeconds,
        overtimeHours: this.overtimeHours,
        regularPay: this.regularPay,
        overtimePay: this.overtimePay,
        totalPayWithOvertime: this.totalPayWithOvertime,
      };
      this.pay.timeClock = timeClock;
      console.log("time clock", this.pay);
      return payService
        .create(this.pay)
        .then(() => {
          this.loading = false;
          this.dialog = false;
          this.$emit("submit");
          this.reset();
        })
        .catch((error) => {
          this.loading = false;
          this.$emit("error", error);
        });
    },
    diffTime() {
      let value = this.workingSeconds - this.totalSeconds;
      return value;
    },
    close() {
      if (this.loading) return;
      this.reset();
      this.dialog = false;
    },
    reset() {
      this.pay.entryAt = this.$moment();
      this.pay.amount = Number(this.grandTotal || 0).toFixed(2);
      this.pay.note = null;
      this.pay.PaidToEmployeeId = this.employee.id;
    },
    secondsToHms(d) {
      d = Number(d);
      let h = Math.floor(d / 3600);
      let m = Math.floor((d % 3600) / 60);
      let s = Math.floor((d % 3600) % 60);

      // var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
      // var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " minutes, ") : "";
      // var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";

      let hDisplay = h > 0 ? h + " h, " : "";
      let mDisplay = m > 0 ? m + " m, " : "";
      let sDisplay = s > 0 ? s + " s " : "";

      return hDisplay + mDisplay + sDisplay;
    },
    // submit() {
    //   this.loading = true;
    //   this.timeClock.EmployeeId = this.selectedEmployee.id;

    //   timeClockService
    //     .create(this.timeClock)
    //     .then(() => {
    //       this.dialog = false;
    //       this.$emit("submit");
    //       this.loading = false;
    //     })
    //     .catch((err) => {
    //       this.$emit("error", err);
    //       this.loading = false;
    //       // console.log("err :: ", err);
    //     });
    // },
  },
};
</script>

<style scoped>
.create-pay-trigger {
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.create-pay-card {
  background: var(--v-background-base) !important;
  border: 1px solid var(--v-border-base);
  border-radius: 22px !important;
  color: var(--v-textPrimary-base);
  overflow: hidden;
}

.create-pay-header {
  align-items: center;
  background: var(--v-surface-base);
  border-bottom: 1px solid var(--v-border-base);
  display: flex;
  gap: 13px;
  padding: 18px 20px;
}

.create-pay-header__icon {
  align-items: center;
  background: rgba(52, 211, 153, 0.12);
  border: 1px solid rgba(52, 211, 153, 0.24);
  border-radius: 14px;
  display: flex;
  flex: 0 0 46px;
  height: 46px;
  justify-content: center;
}

.create-pay-header__icon .v-icon {
  color: var(--v-success-base);
}

.create-pay-header small {
  color: var(--v-textSecondary-base);
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.create-pay-header h2 {
  font-size: 1.2rem;
  line-height: 1.25;
  margin: 1px 0 2px;
}

.create-pay-header p {
  color: var(--v-textSecondary-base);
  font-size: 0.7rem;
  margin: 0;
}

.create-pay-close {
  background: var(--v-surfaceMuted-base);
  color: var(--v-textPrimary-base);
  margin-left: auto;
}

.create-pay-scroll {
  max-height: calc(90vh - 150px);
  overflow: auto;
  padding: 18px;
}

.pay-employee-card,
.pay-review-section,
.pay-entry-section,
.pay-confirmation-note {
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 16px;
}

.pay-employee-card {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  padding: 14px;
}

.pay-employee-avatar {
  align-items: center;
  background: rgba(96, 165, 250, 0.13);
  border: 1px solid rgba(96, 165, 250, 0.25);
  border-radius: 13px;
  color: var(--v-primary-base);
  display: flex;
  flex: 0 0 46px;
  font-size: 1.15rem;
  font-weight: 800;
  height: 46px;
  justify-content: center;
}

.pay-employee-copy {
  min-width: 0;
}

.pay-employee-copy small,
.pay-employee-copy strong,
.pay-employee-copy span,
.pay-employee-total small,
.pay-employee-total strong {
  display: block;
}

.pay-employee-copy small,
.pay-employee-total small {
  color: var(--v-textSecondary-base);
  font-size: 0.65rem;
  font-weight: 700;
}

.pay-employee-copy strong {
  font-size: 0.95rem;
  margin: 1px 0 3px;
}

.pay-employee-copy span {
  align-items: center;
  color: var(--v-textSecondary-base);
  display: flex;
  font-size: 0.68rem;
  gap: 4px;
}

.pay-employee-copy .v-icon {
  color: var(--v-textSecondary-base);
}

.pay-employee-total {
  margin-left: auto;
  text-align: right;
}

.pay-employee-total strong {
  color: var(--v-success-base);
  font-size: 1.3rem;
}

.pay-review-section,
.pay-entry-section {
  margin-bottom: 12px;
  padding: 16px;
}

.pay-section-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 13px;
}

.pay-section-heading small {
  color: var(--v-primary-base);
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pay-section-heading h3 {
  font-size: 1rem;
  margin: 1px 0 0;
}

.pay-overtime-chip {
  background: rgba(96, 165, 250, 0.12) !important;
  color: var(--v-primary-base) !important;
}

.pay-metrics {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.pay-metrics > div {
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 12px;
  min-height: 72px;
  padding: 11px;
}

.pay-metrics span,
.pay-metrics strong {
  display: block;
}

.pay-metrics span {
  color: var(--v-textSecondary-base);
  font-size: 0.65rem;
  margin-bottom: 5px;
}

.pay-metrics strong {
  font-size: 0.79rem;
  line-height: 1.35;
}

.pay-metrics strong small {
  color: var(--v-textSecondary-base);
  font-size: 0.6rem;
}

.pay-variance {
  align-items: center;
  border-radius: 12px;
  display: flex;
  gap: 8px;
  margin-top: 9px;
  padding: 10px 12px;
}

.pay-variance.is-short {
  background: rgba(244, 83, 94, 0.11);
  color: #f36b75;
}

.pay-variance.is-over {
  background: rgba(45, 190, 126, 0.11);
  color: #31c989;
}

.pay-variance .v-icon {
  color: inherit;
}

.pay-variance span {
  font-size: 0.72rem;
}

.pay-variance strong {
  font-size: 0.75rem;
  margin-left: auto;
}

.overtime-summary {
  color: var(--v-textSecondary-base);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.68rem;
  gap: 7px 20px;
  padding: 13px 2px 5px;
}

.pay-breakdown {
  border-top: 1px solid var(--v-border-base);
  margin-top: 10px;
  padding-top: 5px;
}

.pay-breakdown > div {
  align-items: center;
  color: var(--v-textSecondary-base);
  display: flex;
  font-size: 0.76rem;
  justify-content: space-between;
  padding: 7px 2px;
}

.pay-breakdown strong {
  color: var(--v-textPrimary-base);
}

.pay-breakdown .pay-breakdown__total {
  border-top: 1px solid var(--v-border-base);
  color: var(--v-textPrimary-base);
  font-size: 0.86rem;
  font-weight: 800;
  margin-top: 3px;
  padding-top: 11px;
}

.pay-breakdown__total strong {
  color: var(--v-success-base);
  font-size: 1.15rem;
}

.pay-section-help {
  color: var(--v-textSecondary-base);
  font-size: 0.7rem;
  margin: -6px 0 14px;
}

.pay-entry-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.pay-date-field label {
  color: var(--v-textSecondary-base);
  display: block;
  font-size: 0.75rem;
  margin-bottom: 7px;
}

.pay-entry-section ::v-deep .v-input__slot {
  background: var(--v-surfaceMuted-base) !important;
}

.pay-note-field {
  margin-top: 4px;
}

.pay-confirmation-note {
  align-items: center;
  display: flex;
  gap: 11px;
  padding: 13px 15px;
}

.pay-confirmation-note > .v-icon {
  color: var(--v-success-base);
}

.pay-confirmation-note strong,
.pay-confirmation-note span {
  display: block;
}

.pay-confirmation-note strong {
  font-size: 0.76rem;
}

.pay-confirmation-note span {
  color: var(--v-textSecondary-base);
  font-size: 0.66rem;
  margin-top: 2px;
}

.create-pay-actions {
  align-items: center;
  background: var(--v-surface-base);
  border-top: 1px solid var(--v-border-base);
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 18px;
}

.create-pay-actions .v-btn {
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

@media (max-width: 600px) {
  .create-pay-card {
    border: 0;
    border-radius: 0 !important;
    min-height: 100vh;
  }

  .create-pay-header {
    padding: 14px 12px;
  }

  .create-pay-header__icon {
    display: none;
  }

  .create-pay-header h2 {
    font-size: 1.05rem;
  }

  .create-pay-header p {
    display: none;
  }

  .create-pay-scroll {
    max-height: calc(100vh - 136px);
    padding: 10px;
  }

  .pay-employee-card {
    padding: 12px;
  }

  .pay-employee-total strong {
    font-size: 1rem;
  }

  .pay-review-section,
  .pay-entry-section {
    padding: 13px;
  }

  .pay-metrics,
  .pay-entry-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pay-overtime-chip {
    margin: 0;
  }

  .create-pay-actions {
    bottom: 0;
    padding: 10px 12px;
    position: sticky;
  }
}

@media (max-width: 390px) {
  .pay-employee-avatar {
    display: none;
  }

  .pay-metrics {
    grid-template-columns: 1fr 1fr;
  }
}
</style>

<style>
.create-pay-dialog {
  border-radius: 22px !important;
  overflow: hidden;
}

@media (max-width: 600px) {
  .create-pay-dialog {
    border-radius: 0 !important;
  }
}
</style>
